import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { prouniQuery, prouniMutation } from '@/helpers/prouniClient';
import {
  getMyProuniApplication,
  GetMyProuniApplicationResult,
  getMyProuniEligibility,
  GetMyProuniEligibilityResult,
  getProuniDocumentUploadUrl,
  GetProuniDocumentUploadUrlResult,
} from '@/store/graphql/prouni/queries';
import {
  upsertProuniApplication,
  UpsertProuniApplicationResult,
  upsertProuniFamilyMember,
  UpsertProuniFamilyMemberResult,
  removeProuniFamilyMember,
  RemoveProuniFamilyMemberResult,
  submitProuniDocument,
  SubmitProuniDocumentResult,
  removeProuniDocument,
  RemoveProuniDocumentResult,
  submitProuniApplication,
  SubmitProuniApplicationResult,
  exchangeProuniEnrollmentToken,
  ExchangeProuniEnrollmentTokenResult,
} from '@/store/graphql/prouni/mutations';
import { ProuniState } from './state';
import {
  ProuniApplication,
  ProuniDocument,
  ProuniEligibility,
  ProuniFamilyMember,
  UploadDocumentPayload,
} from './types';

const actions: ActionTree<ProuniState, RootState> = {
  // Checks whether the logged e-mail is on the manually-curated candidate
  // list (SisProUni pre-selection). Gates the whole application flow.
  async GET_MY_PROUNI_ELIGIBILITY({ commit }): Promise<ProuniEligibility> {
    const { data } = await prouniQuery<GetMyProuniEligibilityResult>(getMyProuniEligibility);
    const eligibility = data?.getMyProuniEligibility || { isEligible: false };
    commit('SET_PROUNI_ELIGIBILITY', eligibility);
    return eligibility;
  },

  async GET_MY_PROUNI_APPLICATION({ commit }): Promise<ProuniApplication | null> {
    const { data } = await prouniQuery<GetMyProuniApplicationResult>(getMyProuniApplication);
    const application = data?.getMyProuniApplication || null;
    commit('SET_PROUNI_APPLICATION', application);
    return application;
  },

  async UPSERT_PROUNI_APPLICATION(
    { commit, state },
    input: Partial<ProuniApplication>,
  ): Promise<ProuniApplication> {
    const { data } = await prouniMutation<UpsertProuniApplicationResult>(upsertProuniApplication, {
      input,
    });

    const merged = { ...(state.application || {}), ...input, ...data?.upsertProuniApplication };
    commit('SET_PROUNI_APPLICATION', merged);
    return merged;
  },

  async PERSIST_PROUNI_PIPELINE(
    { dispatch },
    payload: { step: string; pipelineState: Record<string, any> },
  ): Promise<void> {
    await dispatch('UPSERT_PROUNI_APPLICATION', {
      pipelineState: { step: payload.step, ...payload.pipelineState },
    });
  },

  async UPSERT_PROUNI_FAMILY_MEMBER(
    { commit },
    input: ProuniFamilyMember,
  ): Promise<ProuniFamilyMember> {
    const { data } = await prouniMutation<UpsertProuniFamilyMemberResult>(
      upsertProuniFamilyMember,
      {
        input,
      },
    );
    const member = data!.upsertProuniFamilyMember;
    commit('UPSERT_PROUNI_FAMILY_MEMBER', member);
    return member;
  },

  async REMOVE_PROUNI_FAMILY_MEMBER({ commit }, id: number): Promise<boolean> {
    const { data } = await prouniMutation<RemoveProuniFamilyMemberResult>(
      removeProuniFamilyMember,
      {
        id,
      },
    );
    const success = Boolean(data?.removeProuniFamilyMember);
    if (success) commit('REMOVE_PROUNI_FAMILY_MEMBER', id);
    return success;
  },

  async UPLOAD_PROUNI_DOCUMENT(
    { commit },
    { file, documentType, familyMemberId, description }: UploadDocumentPayload,
  ): Promise<ProuniDocument> {
    // 1. Ask the backend for a presigned PUT URL.
    const { data: urlData } = await prouniQuery<GetProuniDocumentUploadUrlResult>(
      getProuniDocumentUploadUrl,
      {
        fileName: file.name,
        contentType: file.type,
        documentType,
        familyMemberId: familyMemberId ?? undefined,
      },
    );

    const { uploadUrl, key } = urlData.getProuniDocumentUploadUrl;

    // 2. Upload the raw file straight to S3 (no base64).
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload document (${response.status})`);
    }

    // 3. Persist the document reference (resets status to pending server-side).
    const { data: submitData } = await prouniMutation<SubmitProuniDocumentResult>(
      submitProuniDocument,
      {
        documentType,
        key,
        fileName: file.name,
        mimeType: file.type,
        familyMemberId: familyMemberId ?? undefined,
        description: description ?? undefined,
      },
    );

    const document = submitData!.submitProuniDocument;
    commit('UPSERT_PROUNI_DOCUMENT', document);
    return document;
  },

  async REMOVE_PROUNI_DOCUMENT({ commit }, id: number): Promise<boolean> {
    const { data } = await prouniMutation<RemoveProuniDocumentResult>(removeProuniDocument, {
      id,
    });
    const success = Boolean(data?.removeProuniDocument);
    if (success) commit('REMOVE_PROUNI_DOCUMENT', id);
    return success;
  },

  async SUBMIT_PROUNI_APPLICATION({ commit, state }): Promise<ProuniApplication> {
    const { data } = await prouniMutation<SubmitProuniApplicationResult>(submitProuniApplication);
    const merged = { ...(state.application || {}), ...data?.submitProuniApplication };
    commit('SET_PROUNI_APPLICATION', merged);
    return merged;
  },

  // Ponte ProUni → matrícula: troca a sessão Pocket pelo `admissions-auth-token`
  // da admissão já provisionada e aprovada. O backend recusa quando a conta
  // logada não tem inscrição ProUni aprovada ou quando o cupom é de outro
  // candidato — por isso um cupom vazado não abre matrícula nenhuma.
  async EXCHANGE_PROUNI_ENROLLMENT_TOKEN({ commit }, voucher: string): Promise<string> {
    const { data } = await prouniMutation<ExchangeProuniEnrollmentTokenResult>(
      exchangeProuniEnrollmentToken,
      { voucher },
    );

    const authToken = data?.exchangeProuniEnrollmentToken?.authToken;
    if (!authToken) throw new Error('Prouni enrollment token exchange refused');

    // Reusa a mutation existente: ela já grava o cookie em .descomplica.com.br,
    // que é o dialeto único que o resto do pipeline de matrícula entende.
    commit('SET_AUTH_TOKEN', authToken);

    return authToken;
  },
};

export default actions;
